import type { AssetKind, AssetRef } from "@loumy/contracts";
import { Badge, Button, cx, Icon } from "../ui";
import { downloadResult, resultFilename } from "./download";
import { useResultUrl } from "./ResultUrlContext";

// Datei-Endung je kind für den Download-Namen (presigned-URL trägt die echte Endung nicht
// zuverlässig im Frontend; Vorschau-/Download-Fallback genügt eine plausible Endung).
const KIND_EXT: Record<AssetKind, string> = {
  image: "png",
  spritesheet: "png",
  animation: "mp4",
  video: "mp4",
  audio: "mp3",
};

// §4/D32/D43 — kind-basierter Fallback-Renderer. Anzeige/Download historischer Outputs
// braucht KEINE Capability, nur Asset-Metadaten (kind). Eine ToolUI darf den Renderer
// überschreiben (ResultRenderer); fehlt sie, greift dieser kind-Switch.
//
// D65: liegt für ein Asset eine presigned-GET-`downloadUrl` im ResultUrl-Context (D56), zeigt
// die Kachel die ECHTE Vorschau (img/video/audio je kind); fehlt sie, greift weiter die
// Platzhalter-Kachel (kind/id/Status).

const KIND_ICON: Record<AssetKind, string> = {
  image: "image",
  animation: "film",
  spritesheet: "film",
  video: "video",
  audio: "play",
};

// kinds, die als Video gerendert werden (animation = Loop-Video, D49; video). Rest = Standbild/Audio.
const VIDEO_KINDS: ReadonlySet<AssetKind> = new Set<AssetKind>(["animation", "video"]);

export function checkerStyle(): React.CSSProperties {
  return {
    backgroundImage:
      "linear-gradient(45deg,rgb(var(--border)/.5) 25%,transparent 25%),linear-gradient(-45deg,rgb(var(--border)/.5) 25%,transparent 25%),linear-gradient(45deg,transparent 75%,rgb(var(--border)/.5) 75%),linear-gradient(-45deg,transparent 75%,rgb(var(--border)/.5) 75%)",
    backgroundSize: "16px 16px",
    backgroundPosition: "0 0,0 8px,8px -8px,-8px 0",
  };
}

// `large` (Einzelergebnis): volle Breite + NATÜRLICHE Höhe — ein hohes Bild (z.B. 9:16) überläuft den
// Sichtbereich und scrollt über den äußeren Studio-Container (statt in eine winzige Quadrat-Kachel gequetscht
// zu werden). Im Galerie-Modus (mehrere Outputs) bleibt es `object-contain` in der aspect-square-Kachel.
// `compact` (dichtes Assets-Raster): füllt die quadratische Kachel via `object-cover` — bewusster Beschnitt,
// damit das Raster gleichmäßig quadratisch wirkt (kein Letterbox je nach Asset-Seitenverhältnis).
function Preview({ asset, url, large, compact }: { asset: AssetRef; url: string; large?: boolean; compact?: boolean }) {
  const transparent = asset.kind === "image" || asset.kind === "spritesheet";
  const style = transparent ? checkerStyle() : undefined;
  if (VIDEO_KINDS.has(asset.kind)) {
    // animation/video: Loop-Video — autoplay+muted (Browser-Policy), Controls für Scrubbing.
    // D132: kind 'video' bekommt das Transparenz-Karo statt Schwarz — Alpha-WebM (Video-BG-
    // Remove) zeigt so seine Transparenz; opake Videos decken das Karo ohnehin ab (nur
    // Letterbox-Ränder werden kariert statt schwarz). 'animation' bleibt auf Schwarz.
    return (
      <video
        src={url}
        className={cx(
          // compact: absolut in die aspect-square-Box (Bild trägt NICHT zur Content-Höhe bei,
          // sonst bläht es den grid-Container auf und aspect-square gibt nach → kein Quadrat).
          compact ? "absolute inset-0 h-full w-full object-cover" : cx("w-full object-contain", large ? "max-h-[78vh]" : "h-full"),
          asset.kind === "video" ? "" : "bg-black",
        )}
        style={asset.kind === "video" ? checkerStyle() : undefined}
        autoPlay
        loop
        muted
        playsInline
        controls={!compact}
      />
    );
  }
  if (asset.kind === "audio") {
    return (
      <div className="grid h-full w-full place-items-center p-3">
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <audio src={url} controls className="w-full" />
      </div>
    );
  }
  // image/spritesheet (und unbekannte kinds): Standbild über Transparenz-Karo.
  // draggable=false: das NATIVE img-Drag des Browsers darf den Kachel-Drag (D100,
  // Asset→Ordner) nicht kapern — gezogen wird immer die figure mit unserem MIME-Payload.
  return (
    <img
      src={url}
      alt={asset.kind}
      draggable={false}
      // compact: absolut in die aspect-square-Box (s. Video-Kommentar) → gleichmäßiges Quadrat.
      className={
        compact
          ? "absolute inset-0 h-full w-full object-cover"
          : cx("w-full", large ? "block h-auto" : "h-full object-contain")
      }
      style={style}
    />
  );
}

// Optionaler Drag-Hook (D100): die Assets-Seite macht Kacheln draggable (Asset → Ordner/Inbox
// im FolderTree); ohne Handler bleibt die Kachel statisch (Studio-Ergebnisse unverändert).
type TileDragStart = (asset: AssetRef, e: React.DragEvent) => void;

/**
 * D132: Download-Endung — echte Endung aus dem `r2_key` (z. B. `.webm` beim transparenten
 * Video-BG-Remove-Output), sonst der kind-Fallback (KIND_EXT).
 */
function extOf(asset: AssetRef): string {
  const m = asset.r2_key ? /\.([a-z0-9]{2,5})$/i.exec(asset.r2_key) : null;
  return m?.[1]?.toLowerCase() ?? KIND_EXT[asset.kind] ?? "png";
}

function AssetTile({
  asset,
  large,
  compact,
  onDragStart,
}: {
  asset: AssetRef;
  large?: boolean;
  // `compact` (dichtes Assets-Raster): lässt das kind-Badge weg — im Thumbnail-Grid ist der Typ
  // ohnehin sichtbar (Video spielt, Sheet zeigt das Frame-Raster). Das gibt bei sehr schmalen
  // Kacheln der id-Zeile vollen Platz, statt dass Badge + Download-Button sie aufzehren.
  compact?: boolean;
  onDragStart?: TileDragStart;
}) {
  const url = useResultUrl(asset.id);
  return (
    <figure
      draggable={onDragStart ? true : undefined}
      onDragStart={onDragStart ? (e) => onDragStart(asset, e) : undefined}
      title={onDragStart ? "Ziehen, um das Asset in einen Ordner/die Inbox zu verschieben" : undefined}
      className={cx("overflow-hidden rounded-lg border border-border bg-surface-2", onDragStart && "cursor-grab")}
    >
      <div
        className={cx(
          "grid place-items-center text-muted",
          // Galerie-Kachel = quadratisch; Einzelergebnis = natürliche Höhe (mit Mindesthöhe für den Platzhalter).
          large ? "min-h-[200px]" : "aspect-square",
          // compact: Anker für die absolut gefüllte Vorschau; clippt den object-cover-Überstand.
          compact && "relative overflow-hidden",
        )}
        style={!url && asset.kind === "image" ? checkerStyle() : undefined}
      >
        {url ? (
          <Preview asset={asset} url={url} large={large} compact={compact} />
        ) : (
          // Ohne presigned URL (kein r2_key / R2-Creds fehlen) → Struktur-Platzhalter (D43).
          <Icon name={KIND_ICON[asset.kind] ?? "image"} size={34} />
        )}
      </div>
      <figcaption className="flex items-center justify-between gap-2 border-t border-border px-2.5 py-1.5">
        <span className="min-w-0 truncate font-mono text-[11px] text-muted" title={asset.id}>
          {asset.id.slice(0, 8)}
        </span>
        <div className="flex shrink-0 items-center gap-1.5">
          {!compact && <Badge tone={url ? "accent" : "neutral"}>{asset.kind}</Badge>}
          {url && (
            <Button
              size="sm"
              variant="ghost"
              icon="download"
              onClick={() => void downloadResult(url, resultFilename(asset.kind, asset.id, extOf(asset)))}
              aria-label="Herunterladen"
              title="Ergebnis herunterladen"
            />
          )}
        </div>
      </figcaption>
    </figure>
  );
}

/**
 * `dense` (Assets-Browser, §11): kontrolliertes, responsives Raster mit kleinen Kacheln —
 * `auto-fill,minmax` füllt die Spalten je nach Breite (mehr Assets pro Screen) und unterdrückt
 * den `large`-Einzelfall (im Browser soll ein 1-Asset-Ordner nicht den ganzen Sichtbereich füllen).
 * Ohne `dense` bleibt das Studio/Tool-Verhalten: 1 Ergebnis = groß, Fan-out = 2/3-Spalten-Galerie.
 */
export function FallbackRenderer({
  assets,
  onTileDragStart,
  dense,
}: {
  assets: AssetRef[];
  onTileDragStart?: TileDragStart;
  dense?: boolean;
}) {
  if (assets.length === 0) {
    // Eigene Mindesthöhe (nicht h-full): im natürlichen Fluss (Studio scrollt außen) hätte h-full
    // keinen Höhenbezug und kollabierte auf die Textzeile.
    return (
      <div className="grid min-h-[166px] place-items-center rounded-lg border border-dashed border-border text-sm text-muted">
        Noch kein Ergebnis
      </div>
    );
  }
  // EIN Ergebnis (Normalfall der Generierung) → GROSS in voller Breite/natürlicher Höhe (scrollbar, statt
  // briefmarkengroß in einer Quadrat-Kachel). Mehrere (Fan-out count>1) → kompaktes Galerie-Raster.
  // Im Assets-Browser (`dense`) entfällt der Großfall — dort zählt Dichte, nicht Einzel-Prominenz.
  if (!dense && assets.length === 1) {
    return <AssetTile asset={assets[0]!} large onDragStart={onTileDragStart} />;
  }
  return (
    <div
      className={cx(
        "grid",
        dense ? "grid-cols-[repeat(auto-fill,minmax(8.5rem,1fr))] gap-2.5" : "grid-cols-2 gap-3 sm:grid-cols-3",
      )}
    >
      {assets.map((a) => (
        <AssetTile key={a.id} asset={a} compact={dense} onDragStart={onTileDragStart} />
      ))}
    </div>
  );
}
